import { useEffect, useCallback, useState } from "react"
import { RouterProvider } from "react-router/dom"
import { default as ky } from "ky"

import { ThemeProvider, Theme } from "@f2e/ui/components/Theme"
import { TooltipProvider } from "@f2e/ui/components/base/tooltip"
import { Toaster } from "@f2e/ui/components/base-legacy/toaster"

import { ErrorBoundary } from "@/components/System/index"
import { useStore } from "@/store"
import { SystemConfig } from "@/types"
import { API_PREFIX } from "@/constants"
import { syncPosthogUser } from "@/lib/posthog"

import { Router } from "./router"
import "./App.css"

import { LLMModelResponse, transformLLMModel } from "./hooks/api/llm"

// Create a direct API fetch function that doesn't use React hooks
const fetchAvailableModelsApi = async (
  baseUrl: string,
  modelType?: "text" | "image"
) => {
  try {
    const searchParams = modelType ? `?model_type=${modelType}` : ""
    const response = await ky
      .get(`${baseUrl}${API_PREFIX}/llm/models${searchParams}`)
      .json<{
        data?: LLMModelResponse[]
        error_code: number
        message: string
      }>()

    // Transform snake_case response to camelCase data
    const transformedData = response.data?.map(transformLLMModel) || []
    return { data: transformedData }
  } catch (error) {
    console.error("Failed to fetch available models:", error)
    throw error
  }
}

function App() {
  const [isConfig, setIsConfig] = useState(false)
  const [setSysConfig] = useStore((state) => [state.setSysConfig])
  const setAvailableModels = useStore((state) => state.setAvailableModels)
  const setAvailableImageModels = useStore(
    (state) => state.setAvailableImageModels
  )
  const userMe = useStore((state) => state.userMe)

  const fetchConfig = useCallback(async () => {
    let config: SystemConfig
    try {
      config = (await ky.get(`/config.json?v=1793089158`).json()) as SystemConfig
    } catch (e) {
      config = (await ky.get("/config.default.json").json()) as SystemConfig
    }
    const hostname = window.location.hostname
    const serverConfig = config.serverConfig?.find(
      (config) => config.domain === hostname
    )
    if (serverConfig) {
      config.serverRoot = serverConfig.apiRoot
    }

    setSysConfig(config as SystemConfig)
    setIsConfig(true)
    console.log("config loaded.", config)
  }, [setSysConfig])

  const fetchAvailableModels = useCallback(async () => {
    const config = useStore.getState().sysConfig
    if (!config || !config.serverRoot) {
      console.error("Server configuration not available")
      return
    }

    const [textResult, imageResult] = await Promise.allSettled([
      fetchAvailableModelsApi(config.serverRoot, "text"),
      fetchAvailableModelsApi(config.serverRoot, "image"),
    ])

    if (textResult.status === "fulfilled") {
      const textModels = textResult.value?.data ?? []
      setAvailableModels(textModels)
    } else {
      console.error("Failed to fetch text models:", textResult.reason)
      setAvailableModels([])
    }

    if (imageResult.status === "fulfilled") {
      const imageModels = imageResult.value?.data ?? []
      setAvailableImageModels(imageModels)
    } else {
      console.error("Failed to fetch image models:", imageResult.reason)
      setAvailableImageModels([])
    }
  }, [setAvailableImageModels, setAvailableModels])

  useEffect(() => {
    console.log("App mounted!")
    fetchConfig()
  }, [fetchConfig])

  // Keep PostHog user identity in sync with auth state
  useEffect(() => {
    syncPosthogUser(userMe)
  }, [userMe])

  // Only fetch available models after config is loaded
  useEffect(() => {
    if (isConfig) {
      fetchAvailableModels()
    }
  }, [isConfig, fetchAvailableModels])

  return (
    <ThemeProvider defaultTheme={Theme.System} storageKey="vite-ui-theme">
      {isConfig ? (
        <TooltipProvider>
          <Toaster />
          <ErrorBoundary>
            <RouterProvider router={Router} />
          </ErrorBoundary>
        </TooltipProvider>
      ) : null}
    </ThemeProvider>
  )
}

export default App
